import React, { useState, useEffect, useMemo } from 'react';
import { api } from '../../api';
import FormRenderer from '../../components/forms/FormRenderer';
import ModalRoot from '../../components/ModalRoot';

/**
 * TransitionModal — Shows when moving between stages.
 *
 * Two shapes for `transition.required_fields`:
 *
 *   1. LEGACY (existing workflows pre-A5): flat array
 *        [{ key, label, type, required, required_if, options }]
 *      Renders inline using the bespoke field map below. Field types:
 *      text, textarea, number, select, vsf_select, property_select.
 *
 *   2. PLATFORM (A5+, FormSchema-shaped):
 *        { version, title?, sections: [{ title?, fields: [...] }] }
 *      Rendered through <FormRenderer compact /> — same engine the inflow
 *      forms use. Supports show_if conditional visibility, type-aware
 *      validation, all 10+ field types incl. picker sub-types and files.
 *
 * Detection is shape-based — the server keeps both shapes around so any
 * existing transition keeps working unchanged.
 */
export default function TransitionModal({ transition, onConfirm, onClose, loading }) {
  const requiredFields = transition?.required_fields ?? null;

  const isFormSchema = useMemo(() => {
    return !!(requiredFields && typeof requiredFields === 'object'
      && !Array.isArray(requiredFields)
      && Array.isArray(requiredFields.sections));
  }, [requiredFields]);

  // ── Notes (shared across both shapes) ─────────────────────────────
  const [notes, setNotes] = useState('');

  if (isFormSchema) {
    return (
      <FormSchemaTransition
        transition={transition}
        schema={requiredFields}
        notes={notes}
        onNotesChange={setNotes}
        onConfirm={onConfirm}
        onClose={onClose}
        loading={loading}
      />
    );
  }

  return (
    <LegacyArrayTransition
      transition={transition}
      requiredFields={Array.isArray(requiredFields) ? requiredFields : []}
      notes={notes}
      onNotesChange={setNotes}
      onConfirm={onConfirm}
      onClose={onClose}
      loading={loading}
    />
  );
}

// ─────────────────────────────────────────────────────────────────────
// Platform path — FormRenderer (compact)
// ─────────────────────────────────────────────────────────────────────
function FormSchemaTransition({ transition, schema, notes, onNotesChange, onConfirm, onClose, loading }) {
  const [values, setValues] = useState({});
  const [files, setFiles] = useState({});

  const handleSubmit = async (submittedValues, submittedFiles) => {
    onConfirm({
      field_values: submittedValues,
      files: submittedFiles,
      notes: notes.trim() || null,
    });
  };

  return (
    <ModalRoot onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 640, maxHeight: '90vh', overflowY: 'auto' }}>
        <div className="modal-header">
          <h3 style={{ margin: 0 }}>Move to: {transition?.to_stage_name}</h3>
          <button className="btn btn-sm" onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer' }}>✕</button>
        </div>

        <div style={{ padding: '12px 20px 0' }}>
          <FormRenderer
            schema={schema}
            values={values}
            onChange={setValues}
            files={files}
            onFilesChange={setFiles}
            compact
            submitLabel={null}
          />
        </div>

        <div style={{ padding: '0 20px 16px' }}>
          <label style={{ display: 'block', fontWeight: 600, marginBottom: 4, fontSize: 13, color: '#374151' }}>
            Notes <span style={{ fontWeight: 400, color: '#9ca3af' }}>(optional)</span>
          </label>
          <textarea
            className="sales-input"
            value={notes}
            onChange={e => onNotesChange(e.target.value)}
            rows={2}
            placeholder="Add a note about this transition..."
            style={{ width: '100%', resize: 'vertical' }}
          />
        </div>

        <div style={{ padding: '12px 20px', borderTop: '1px solid #e5e7eb', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" className="btn" onClick={onClose} style={{ background: '#f3f4f6', color: '#374151' }}>Cancel</button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={loading}
            onClick={() => handleSubmit(values, files)}
            style={{ backgroundColor: transition?.to_stage_color || '#3b82f6' }}
          >
            {loading ? 'Moving...' : `Move to ${transition?.to_stage_name}`}
          </button>
        </div>
      </div>
    </ModalRoot>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Legacy path — flat array of { key, label, type, required, required_if }
// (untouched from pre-A5 — keeps Refund / Take Back / etc. working)
// ─────────────────────────────────────────────────────────────────────
function LegacyArrayTransition({ transition, requiredFields, notes, onNotesChange, onConfirm, onClose, loading }) {
  const [fieldValues, setFieldValues] = useState({});

  const [vsfLocations, setVsfLocations] = useState([]);
  const [properties, setProperties] = useState([]);
  const [propertySearch, setPropertySearch] = useState('');

  useEffect(() => {
    const needsVsf = requiredFields.some(f => f.type === 'vsf_select');
    const needsProps = requiredFields.some(f => f.type === 'property_select');
    if (needsVsf) api.getVsfLocations?.().then(setVsfLocations).catch(() => {});
    if (needsProps) api.getProperties?.({ limit: 100 }).then(d => {
      setProperties(Array.isArray(d) ? d : (d?.properties || []));
    }).catch(() => {});
  }, [transition?.id, requiredFields]);

  useEffect(() => {
    if (!propertySearch) return;
    const t = setTimeout(() => {
      api.getProperties?.({ search: propertySearch, limit: 100 }).then(d => {
        setProperties(Array.isArray(d) ? d : (d?.properties || []));
      }).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [propertySearch]);

  function isConditionallyRequiredSatisfied(field) {
    if (!field.required_if) return field.required;
    const [otherKey, expected] = String(field.required_if).split('=');
    return fieldValues[otherKey] === expected;
  }

  function isVisible(field) {
    if (!field.required_if) return true;
    const [otherKey, expected] = String(field.required_if).split('=');
    return fieldValues[otherKey] === expected;
  }

  const handleSubmit = (e) => {
    e.preventDefault();
    for (const field of requiredFields) {
      const effectiveRequired = field.required_if
        ? isConditionallyRequiredSatisfied(field)
        : field.required;
      if (!effectiveRequired) continue;
      const v = fieldValues[field.key];
      if (v == null || String(v).trim() === '') {
        alert(`"${field.label}" is required`);
        return;
      }
    }
    onConfirm({ field_values: fieldValues, notes: notes.trim() || null });
  };

  function renderField(field) {
    const val = fieldValues[field.key] ?? '';
    const setVal = v => setFieldValues(prev => ({ ...prev, [field.key]: v }));
    const common = {
      className: 'sales-input',
      value: val,
      onChange: e => setVal(e.target.value),
      style: { width: '100%' },
    };

    switch (field.type) {
      case 'textarea':
        return <textarea {...common} rows={3} style={{ ...common.style, resize: 'vertical' }} />;

      case 'select':
        return (
          <select {...common}>
            <option value="">Select…</option>
            {(field.options || []).map(o => {
              const optValue = typeof o === 'string' ? o : o.value;
              const optLabel = typeof o === 'string' ? o : (o.label || o.value);
              return <option key={optValue} value={optValue}>{optLabel}</option>;
            })}
          </select>
        );

      case 'vsf_select':
        return (
          <select {...common}>
            <option value="">Select VSF…</option>
            {vsfLocations.map(v => (
              <option key={v.id} value={v.id}>{v.name} — {v.address}</option>
            ))}
          </select>
        );

      case 'property_select':
        return (
          <>
            <input
              type="text"
              className="sales-input"
              placeholder="Search property…"
              value={propertySearch}
              onChange={e => setPropertySearch(e.target.value)}
              style={{ width: '100%', marginBottom: 6 }}
            />
            <select {...common}>
              <option value="">Select property…</option>
              {properties.map(p => (
                <option key={p.id} value={p.id}>{p.name}{p.address ? ` — ${p.address}` : ''}</option>
              ))}
            </select>
          </>
        );

      case 'number':
        return <input {...common} type="number" />;

      default:
        return <input {...common} type="text" />;
    }
  }

  return (
    <ModalRoot onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <div className="modal-header">
          <h3 style={{ margin: 0 }}>Move to: {transition?.to_stage_name}</h3>
          <button className="btn btn-sm" onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer' }}>✕</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
            {requiredFields.map(field => {
              if (!isVisible(field)) return null;
              const effectiveRequired = field.required_if
                ? isConditionallyRequiredSatisfied(field)
                : field.required;
              return (
                <div key={field.key}>
                  <label style={{ display: 'block', fontWeight: 600, marginBottom: 4, fontSize: 13, color: '#374151' }}>
                    {field.label} {effectiveRequired && <span style={{ color: '#ef4444' }}>*</span>}
                  </label>
                  {renderField(field)}
                </div>
              );
            })}

            <div>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: 4, fontSize: 13, color: '#374151' }}>
                Notes <span style={{ fontWeight: 400, color: '#9ca3af' }}>(optional)</span>
              </label>
              <textarea
                className="sales-input"
                value={notes}
                onChange={e => onNotesChange(e.target.value)}
                rows={2}
                placeholder="Add a note about this transition..."
                style={{ width: '100%', resize: 'vertical' }}
              />
            </div>
          </div>

          <div style={{ padding: '12px 20px', borderTop: '1px solid #e5e7eb', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" className="btn" onClick={onClose} style={{ background: '#f3f4f6', color: '#374151' }}>
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
              style={{ backgroundColor: transition?.to_stage_color || '#3b82f6' }}
            >
              {loading ? 'Moving...' : `Move to ${transition?.to_stage_name}`}
            </button>
          </div>
        </form>
      </div>
    </ModalRoot>
  );
}
